import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { useLang } from "@/context/LangContext";

const HERO_VISUAL =
  "https://static.prod-images.emergentagent.com/jobs/c4123765-4681-4e75-86dd-45916bba434c/images/06027a5a28d0e8baec6ce8cc480c8f4dd1673451e36ccd8cf2a7d3f5698a6328.png";

const Hero = () => {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 200]);
  const yVisual = useTransform(scrollYProgress, [0, 1], [0, -120]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      ref={ref}
      id="top"
      className="relative pt-[140px] pb-32 md:pt-[180px] md:pb-40 overflow-hidden"
    >
      {/* Background glows */}
      <div
        className="glow-orb"
        style={{ width: 520, height: 520, background: "rgba(16,185,129,0.35)", top: -120, left: -100 }}
      />
      <div
        className="glow-orb"
        style={{ width: 420, height: 420, background: "rgba(4,120,87,0.25)", top: 240, right: -80 }}
      />

      {/* Floating background visual */}
      <motion.div
        style={{ y: yVisual, opacity }}
        className="absolute inset-0 pointer-events-none flex items-center justify-center"
        aria-hidden
      >
        <img
          src={HERO_VISUAL}
          alt=""
          className="absolute right-[-10%] top-[14%] w-[120%] md:w-[80%] max-w-[1200px] opacity-70 mix-blend-screen select-none"
          draggable={false}
        />
      </motion.div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10 z-10">
        <motion.div style={{ y }} className="max-w-5xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="inline-flex items-center gap-2 text-[11px] tracking-[0.3em] uppercase text-po-accent mb-8"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-po-accent shadow-[0_0_12px_rgba(16,185,129,0.8)]" />
            {t.hero.eyebrow}
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
            className="font-display font-bold tracking-[-0.04em] text-white text-[44px] leading-[0.95] sm:text-6xl md:text-7xl lg:text-[96px]"
          >
            {t.hero.titleA}
            <br />
            {t.hero.titleB}{" "}
            <span className="font-serif-it text-po-accent">{t.hero.titleC}</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.45 }}
            className="mt-8 max-w-2xl text-po-text2 text-base sm:text-lg leading-relaxed"
          >
            {t.hero.sub}
          </motion.p>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="mt-4 text-sm text-po-accent/80"
          >
            {t.hero.microNote}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.7 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              onClick={() => scrollTo("contact")}
              data-testid="hero-cta-start-earning"
              className="btn-glow inline-flex items-center gap-2 bg-po-accent text-[#04140C] font-semibold rounded-full px-7 py-4 text-[15px]"
            >
              {t.hero.ctaPrimary}
              <ArrowUpRight className="h-4 w-4" />
            </button>
            <button
              onClick={() => scrollTo("how")}
              data-testid="hero-cta-see-how"
              className="inline-flex items-center gap-2 rounded-full px-7 py-4 text-[15px] border border-white/10 text-white hover:bg-white/[0.04] hover:border-po-accent/30 backdrop-blur-md transition-all"
            >
              {t.hero.ctaSecondary}
              <ArrowDown className="h-4 w-4" />
            </button>
          </motion.div>

          {/* Stats strip */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.9 }}
            className="mt-20 grid grid-cols-3 max-w-2xl gap-6 md:gap-10 border-t border-white/[0.06] pt-8"
          >
            {t.hero.stats.map((s, i) => (
              <div key={i} data-testid={`hero-stat-${i}`}>
                <div className="font-display text-3xl md:text-5xl font-semibold text-white tracking-tight">
                  {s.k}
                </div>
                <div className="mt-2 text-[11px] uppercase tracking-[0.2em] text-po-text3">{s.v}</div>
              </div>
            ))}
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
